import {
  apiErrorSchema,
  buildPath,
  routes,
  type ErrorCode,
  type RouteBody,
  type RouteName,
  type RouteResponse,
} from '@split/shared';

/** An error answered by the API (`{ error: { code, message } }`) or a network failure. */
export class ApiError extends Error {
  readonly code: ErrorCode | 'NETWORK';
  readonly status: number;
  readonly details: unknown;
  readonly retryAfter: number | null;

  constructor(
    code: ErrorCode | 'NETWORK',
    status: number,
    message: string,
    details?: unknown,
    retryAfter: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.retryAfter = retryAfter;
  }
}

export function isApiError(e: unknown, code?: ErrorCode | 'NETWORK'): e is ApiError {
  return e instanceof ApiError && (code === undefined || e.code === code);
}

type RouteDefOf<K extends RouteName> = (typeof routes)[K];

export interface RequestOptions<K extends RouteName> {
  params?: Readonly<Record<string, string>>;
  query?: Readonly<Record<string, string | number | boolean | undefined>>;
  body?: RouteDefOf<K> extends { body: unknown } ? RouteBody<K> : never;
  /** Raw body (receipt upload). */
  raw?: { data: Blob; contentType: string };
  signal?: AbortSignal;
}

const unauthenticatedListeners = new Set<() => void>();
/** Called on any 401 so the app can drop the cached session. */
export function onUnauthenticated(cb: () => void): () => void {
  unauthenticatedListeners.add(cb);
  return () => unauthenticatedListeners.delete(cb);
}

/** Typed fetch for a route of `@split/shared`. Cookies carry the session (same origin). */
export async function api<K extends RouteName>(
  name: K,
  options: RequestOptions<K> = {},
): Promise<RouteResponse<K>> {
  const def = routes[name];
  let url = buildPath(def.path, options.params);
  if (options.query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(options.query)) if (v !== undefined) qs.set(k, String(v));
    const s = qs.toString();
    if (s) url += `?${s}`;
  }
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (options.raw) {
    headers['Content-Type'] = options.raw.contentType;
    body = options.raw.data;
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method: def.method,
      headers,
      body,
      credentials: 'same-origin',
      signal: options.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError('NETWORK', 0, e instanceof Error ? e.message : 'Network error');
  }

  const text = await res.text();
  let json: unknown = undefined;
  if (text !== '') {
    try {
      json = JSON.parse(text);
    } catch {
      json = undefined;
    }
  }

  if (!res.ok) {
    const parsed = apiErrorSchema.safeParse(json);
    const retry = res.headers.get('Retry-After');
    const err = parsed.success
      ? new ApiError(
          parsed.data.error.code,
          res.status,
          parsed.data.error.message,
          parsed.data.error.details,
          retry ? Number(retry) : null,
        )
      : new ApiError(
          res.status === 401 ? 'UNAUTHENTICATED' : res.status >= 500 ? 'INTERNAL' : 'BAD_REQUEST',
          res.status,
          `HTTP ${res.status}`,
        );
    if (err.code === 'UNAUTHENTICATED' || err.code === 'ACCOUNT_DISABLED') {
      for (const cb of unauthenticatedListeners) cb();
    }
    throw err;
  }

  if (import.meta.env.DEV) {
    const check = def.response.safeParse(json);
    if (!check.success) {
      console.warn(`[api] ${name}: response does not match the contract`, check.error.issues);
    }
  }
  return json as RouteResponse<K>;
}
